import { create } from 'zustand';
import type { DatovkaApiType } from '@gov-nx/api/types/databoxTypes';
import { is, prop, propEq } from '@gov-nx/core/types/helpers.types';
import type { DataBoxState } from './store.types';

const initialState = {
	isLoaded: false,
	list: [],
	credit: {},
	lastOpenedDatabox: undefined,
	isStorageAccordionOpen: true,
	isDataboxFormDirty: false,
};

const useDataBoxStoreBase = create<DataBoxState>()((set) => ({
	...initialState,

	setDataBoxList: (list: DatovkaApiType[]) => set({ list, isLoaded: true }),
	dataBoxReset: () => set({ ...initialState }),
	removeDataBox: (databoxId: string) =>
		set((state: DataBoxState) => ({
			list: state.list.filter((item) => item.datovaSchrankaId !== databoxId),
		})),
	setLastOpenedDatabox: (databoxId: string) =>
		set({ lastOpenedDatabox: databoxId }),
	setIsStorageAccordionOpen: (isStorageAccordionOpen: boolean) =>
		set({ isStorageAccordionOpen }),
	setIsDataboxFormDirty: (isDataboxFormDirty: boolean) =>
		set({ isDataboxFormDirty }),
}));

export const useDataBoxStore = <T extends keyof DataBoxState>(
	property: T
): DataBoxState[T] => useDataBoxStoreBase(prop(property));

export const useDataBoxStoreLists = () => {
	const list = useDataBoxStore('list');
	const connectedDataBoxes = list.filter(propEq('odpojeno', false));
	return {
		list,
		connectedDataBoxes,
		connectedDataBoxesIds: connectedDataBoxes
			.map(prop('datovaSchrankaId'))
			.filter(is),
		disconnectedDataBoxes: list.filter(
			propEq('odpojeno', true) || propEq('odpojeno', undefined)
		),
		connectedAndDisconnectedDataBoxes: [
			...list.filter(propEq('odpojeno', false)),
			...list.filter(propEq('odpojeno', true)),
		],
		dataBoxesToConnect: list
			.filter((databox) =>
				databox?.stav ? ['EXISTUJE', 'ZALOZENA'].includes(databox.stav) : false
			)
			.filter((databox) => databox.odpojeno !== false),
		dataBoxesToCreate: list.filter(propEq('stav', 'LZE_ZALOZIT')),
		getConnectedDataBoxesByTypes: (types: DatovkaApiType['typSchranky'][]) =>
			connectedDataBoxes.filter((databox) =>
				types.includes(databox.typSchranky)
			),

		getHasConnectedDatabox: () => connectedDataBoxes.length > 0,
		getDataBoxById: (id: DatovkaApiType['datovaSchrankaId']) =>
			list.find(propEq('datovaSchrankaId', id)),
		getDataBoxByType: (type: DatovkaApiType['typSchranky']) =>
			list.find(propEq('typSchranky', type)),
	};
};
